import api from "./api";
import { API_BASE_URL, normalizeApiMediaUrl } from "./constant";

const getError = (error, fallback) => error.response?.data?.message || error.response?.data?.error || fallback;

function appendFiles(formData, files = []) {
  Array.from(files || []).forEach((file) => formData.append("attachments", file));
}

async function getDashboard() {
  try {
    const { data } = await api.get("/api/support/dashboard");
    return { success: true, data };
  } catch (error) {
    return { success: false, error: getError(error, "Could not load support dashboard") };
  }
}

async function getTickets(params = {}) {
  try {
    const { data } = await api.get("/api/support/tickets", { params });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: getError(error, "Could not load support tickets") };
  }
}

async function createTicket(payload, files = []) {
  try {
    const formData = new FormData();
    const normalizedPayload = {
      ...payload,
      customDepartment: payload.department === "OTHER" ? String(payload.customDepartment || "").trim() : ""
    };
    Object.entries(normalizedPayload).forEach(([key, value]) => formData.append(key, value));
    appendFiles(formData, files);
    const { data } = await api.post("/api/support/tickets", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: getError(error, "Could not create ticket") };
  }
}

async function getTicket(ticketId) {
  try {
    const { data } = await api.get(`/api/support/tickets/${ticketId}`);
    return { success: true, data };
  } catch (error) {
    return { success: false, error: getError(error, "Could not load ticket") };
  }
}

async function sendMessage(ticketId, message, files = [], internalNote = false) {
  try {
    const formData = new FormData();
    formData.append("message", message);
    formData.append("internalNote", internalNote);
    appendFiles(formData, files);
    const { data } = await api.post(`/api/support/tickets/${ticketId}/messages`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: getError(error, "Could not send reply") };
  }
}

async function updateStatus(ticketId, status, note = "") {
  try {
    const { data } = await api.patch(`/api/support/tickets/${ticketId}/status`, { status, note });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: getError(error, "Could not update ticket status") };
  }
}

async function assignTicket(ticketId, adminId) {
  try {
    const { data } = await api.patch(`/api/support/admin/tickets/${ticketId}/assign`, { adminId });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: getError(error, "Could not assign ticket") };
  }
}

async function mergeTickets(sourceTicketId, targetTicketId, note = "") {
  try {
    const { data } = await api.post("/api/support/admin/tickets/merge", { sourceTicketId, targetTicketId, note });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: getError(error, "Could not merge tickets") };
  }
}

async function getUnreadCount() {
  try {
    const { data } = await api.get("/api/support/unread-count", { skipAuthRedirect: true });
    return { success: true, data: data.count || 0 };
  } catch (error) {
    return { success: false, error: getError(error, "Could not load unread count") };
  }
}

async function fetchAttachmentBlob(url) {
  const attachmentUrl = normalizeApiMediaUrl(url);
  const requestUrl =
    API_BASE_URL && attachmentUrl?.startsWith(API_BASE_URL)
      ? attachmentUrl.slice(API_BASE_URL.length) || "/"
      : attachmentUrl;
  const { data } = await api.get(requestUrl, { responseType: "blob" });
  return URL.createObjectURL(data);
}

export const supportService = {
  getDashboard,
  getTickets,
  createTicket,
  getTicket,
  sendMessage,
  updateStatus,
  assignTicket,
  mergeTickets,
  getUnreadCount,
  fetchAttachmentBlob
};
